import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAllShows } from "../api/client";
import RecoveryPanel from "../components/RecoveryPanel";
import ShowCard from "../components/ShowCard";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Chip from "../components/ui/Chip";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import { MOOD_OPTIONS } from "../constants/recommendationOptions";
import { cn } from "../utils/classNames";
import { listText, showPath } from "../utils/showDisplay";

const moodBoardConcepts = MOOD_OPTIONS.filter(
  ({ value }) => !value.includes("wildcard")
).map(({ label, value }) => ({
  id: value.join("-"),
  label,
  values: value,
}));

function normalizeMood(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .trim();
}

function dedupeShows(shows = []) {
  const seen = new Set();

  return shows.filter((show) => {
    const key = normalizeMood(show.title || show.id);
    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

function showMatchesMoodConcept(show = {}, concept = {}) {
  const showMoods = new Set((show.moods || []).map(normalizeMood));
  return concept.values.some((mood) => showMoods.has(normalizeMood(mood)));
}

function buildMoodBoards(catalog = []) {
  return moodBoardConcepts
    .map((concept) => {
      const shows = dedupeShows(catalog.filter((show) => showMatchesMoodConcept(show, concept)));

      return {
        ...concept,
        shows,
      };
    })
    .filter((board) => board.shows.length > 0);
}

export default function MoodBoard() {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Loading mood boards...");
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [reloadAttempt, setReloadAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    setStatus("loading");
    setMessage("Loading mood boards...");

    fetchAllShows({ limit: 100, sort: "title" })
      .then((response) => {
        if (!active) return;

        const results = response?.data?.results;
        if (!Array.isArray(results)) {
          setCatalog([]);
          setStatus("malformed");
          setMessage("Mood Board returned an unexpected catalog response.");
          return;
        }

        setCatalog(results);
        setStatus("success");
        setMessage("");
      })
      .catch((err) => {
        if (!active) return;

        setCatalog([]);
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Mood Board could not load.");
      });

    return () => {
      active = false;
    };
  }, [reloadAttempt]);

  const boards = useMemo(() => buildMoodBoards(catalog), [catalog]);
  const selectedBoard = boards.find((board) => board.id === selectedBoardId) || boards[0] || null;
  const totalBoardTitles = boards.reduce((total, board) => total + board.shows.length, 0);
  const hasRecoveryError = status === "error" || status === "malformed";

  const retryMoodBoard = () => {
    setReloadAttempt((attempt) => attempt + 1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-end">
        <div>
          <Badge variant="gold" className="mb-5">Mood Board</Badge>
          <h1 className="font-display text-4xl font-bold leading-tight text-hookd-cream sm:text-5xl">
            Pick the energy first.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-hookd-muted">
            Mood-led shelves pulled from the same signals behind your recommendations.
          </p>
        </div>

        <Button as={Link} to="/quiz" variant="secondary" className="w-full">
          Take the Vibe Quiz
        </Button>
      </div>

      {status === "loading" && (
        <LoadingState title="Loading mood boards" message={message} />
      )}

      {hasRecoveryError && (
        <RecoveryPanel
          title={status === "malformed" ? "Mood Board could not be read" : "Mood Board did not load"}
          message={message}
          variant="error"
          actions={[
            { label: "Try Again", onClick: retryMoodBoard },
            { label: "Discover Shows", to: "/discover" },
          ]}
        />
      )}

      {status === "success" && boards.length === 0 && (
        <EmptyState
          title="No mood boards yet"
          message="The catalog needs titles with matching mood signals before this shelf can open."
          primaryAction={{ label: "Discover Shows", to: "/discover" }}
          secondaryAction={{ label: "Try Again", onClick: retryMoodBoard }}
        />
      )}

      {status === "success" && selectedBoard && (
        <div className="grid gap-8">
          <section aria-label="Mood collections">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-hookd-muted">
                <span className="font-bold text-hookd-empress">{boards.length}</span>{" "}
                boards with{" "}
                <span className="font-bold text-hookd-empress">{totalBoardTitles}</span>{" "}
                title matches
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {boards.map((board) => {
                const isSelected = board.id === selectedBoard.id;

                return (
                  <Card
                    key={board.id}
                    as="button"
                    type="button"
                    interactive
                    onClick={() => setSelectedBoardId(board.id)}
                    aria-pressed={isSelected}
                    aria-label={`${board.label}, ${board.shows.length} titles`}
                    className={cn(
                      "min-h-44 p-5 text-left",
                      isSelected && "border-hookd-empress bg-hookd-empress/15 shadow-glow"
                    )}
                  >
                    <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-md border border-hookd-gold/40 bg-hookd-gold/10 text-xl text-hookd-gold">
                      ✧
                    </span>
                    <h2 className="font-display text-2xl font-bold leading-tight text-hookd-cream">
                      {board.label}
                    </h2>
                    <p className="mt-3 text-sm font-semibold text-hookd-empress">
                      {board.shows.length} titles
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {board.values.map((mood) => (
                        <Chip key={mood} variant="filter">
                          {mood}
                        </Chip>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)]" data-testid="selected-mood-board">
            <aside>
              <Card className="p-5">
                <h2 className="font-display text-2xl font-bold text-hookd-cream">
                  {selectedBoard.label}
                </h2>
                <dl className="mt-5 grid gap-4 text-sm">
                  <div>
                    <dt className="font-bold text-hookd-gold">Mood Signals</dt>
                    <dd className="text-hookd-muted">{listText(selectedBoard.values)}</dd>
                  </div>
                  <div>
                    <dt className="font-bold text-hookd-gold">Titles</dt>
                    <dd className="text-hookd-muted">{selectedBoard.shows.length}</dd>
                  </div>
                </dl>
                <Button
                  as={Link}
                  to="/quiz"
                  state={{
                    moodSeed: {
                      label: selectedBoard.label,
                      value: selectedBoard.values,
                    },
                  }}
                  className="mt-5 w-full"
                >
                  Get Recs For This Mood
                </Button>
                <Button
                  as={Link}
                  to="/discover"
                  variant="ghost"
                  className="mt-3 w-full"
                >
                  Open Discover
                </Button>
              </Card>
            </aside>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {selectedBoard.shows.map((show) => (
                <ShowCard
                  key={`${selectedBoard.id}-${show.id || show.title}`}
                  show={show}
                  detailsTo={showPath(show)}
                />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
